



































































































































































































































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import { mapGetters, mapState } from 'vuex';

import { shouldHideMonthlyPlansFromPaywall } from 'frontend/utils/premium-referrals';
import {
  Coupon,
  MobileGetter,
  Optional,
  User,
  PaywallPlansInfo,
  PaywallPlanObject,
} from 'frontend/types';
import { Discount } from 'frontend/types/premium';
import {
  isLegacyDirectTraffic,
  isLegacySeasonal,
  isStudentDiscount,
} from 'frontend/utils/discounts';
import { safeWindow } from 'frontend/utils/window';
import PaywallPlan from 'frontend/views/premium/PaywallPlan.vue';
import PaywallSubscriptionNotice from 'frontend/views/premium/components/PaywallSubscriptionNotice.vue';
import PaywallPlanMini from 'frontend/views/premium/components/PaywallPlanMini.vue';
import { addToCartTrack } from 'frontend/utils/analytics';

@Component({
  components: {
    PaywallPlan,
    PaywallPlanMini,
    PaywallSubscriptionNotice,
  },
  computed: {
    ...mapState('premiumPurchaseInfo', ['is_discounted']),
    ...mapState('pageHeader', ['context']),
    ...mapState('paywall', ['paywall_type']),
    ...mapGetters('pageHeader', ['isMobile', 'coupon']),
    ...mapGetters('globals', ['discount']),
    ...mapState('globals', ['user']),
    ...mapGetters('paywall', ['paywallPlans']),
  },
})
export default class PaywallPlans extends Vue {
  @Prop({ type: Boolean, default: false }) private readonly upgrade!: boolean;

  private readonly paywallPlans!: PaywallPlansInfo;
  private readonly context: any;
  private readonly coupon!: Coupon;
  private readonly user!: User;
  private readonly isMobile!: MobileGetter;
  // tslint:disable-next-line:variable-name
  private readonly is_discounted!: boolean;
  private readonly discount!: Optional<Discount>;
  // tslint:disable-next-line:variable-name
  private paywall_type!: string;

  private showLegal = false;

  private get trialLength(): number {
    return this.user.trial_length;
  }

  private get directTrafficDiscount(): boolean {
    return isLegacyDirectTraffic(this.discount);
  }

  private get monthlyPlan(): Optional<PaywallPlanObject> {
    if (shouldHideMonthlyPlansFromPaywall(this.context)) {
      return undefined;
    }
    return this.paywallPlans.monthlyPlan;
  }

  private get seasonalDiscount(): boolean {
    return isLegacySeasonal(this.discount);
  }

  private get isStudentDiscount(): boolean {
    return isStudentDiscount(this.discount);
  }

  private get experimentAwareTrialPlanPriceMessage(): string {
    const asterisk = this.paywallPlans.annualPlanIncludesReferralDiscount ? '*' : '';
    return `${this.paywallPlans?.annualPlan?.display_price}${asterisk}/month after free trial (billed annually)`;
  }

  private axId(interval: string): string {
    if (interval === 'lifetime') {
      return 'clicked_b2_lifetime_upgrade_chargebee';
    }
    const intervalComponent =
      interval === 'annual' ? 'annual' : interval === 'monthly' ? 'monthly' : undefined;

    return `clicked_b2_${intervalComponent}_logged_in_chargebee`;
  }

  private handleAnnualClick() {
    if (!this.user.is_authenticated) {
      this.$store.dispatch('signupModal/signup');
    } else if (this.paywallPlans.annualPlan) {
      addToCartTrack();
      const win = safeWindow();
      if (win) {
        (win as any).location.href = this.paywallPlans.annualPlan.checkout_url;
      }
    } else {
      addToCartTrack();
      this.safeNav(this.paywallPlans.annualPlan!.checkout_url);
    }
  }

  private handleMonthlyClick(): void {
    if (!this.user.is_authenticated) {
      this.$store.dispatch('signupModal/signup');
    } else if (this.monthlyPlan) {
      addToCartTrack();
      const win = safeWindow();
      if (win) {
        (win as any).location.href = this.monthlyPlan.checkout_url;
      }
    } else {
      addToCartTrack();
      this.safeNav(this.monthlyPlan!.checkout_url);
    }
  }

  private handleLifetimeClick() {
    if (!this.user.is_authenticated) {
      this.$store.dispatch('signupModal/signup');
    } else if (this.paywallPlans.lifetimePlan) {
      addToCartTrack();
      const win = safeWindow();
      if (win) {
        (win as any).location.href = this.paywallPlans.lifetimePlan.checkout_url;
      }
    } else {
      addToCartTrack();
      this.safeNav(this.paywallPlans.lifetimePlan!.checkout_url);
    }
  }

  private handleSubscribeNowClick(): void {
    if (!this.user.is_authenticated) {
      this.$store.dispatch('signupModal/signup');
    } else {
      addToCartTrack();
      this.safeNav(this.paywallPlans.annualPlanDirect!.checkout_url);
    }
  }

  private safeNav(url: string): void {
    const safeWin = safeWindow();
    if (safeWin) {
      safeWin.location.href = url;
    }
  }

  private get strikeThroughPrice(): string | null {
    if (
      !this.directTrafficDiscount ||
      this.discount?.is_visible ||
      this.paywallPlans.annualPlanIncludesReferralDiscount
    ) {
      // fallback to usual behavior
      if (this.paywallPlans.annualPlan && this.paywallPlans.annualPlan.strikethrough_price) {
        return this.paywallPlans.annualPlan.strikethrough_price;
      }
      if (this.paywallPlans.lifetimePlan && this.paywallPlans.lifetimePlan.strikethrough_price) {
        return this.paywallPlans.lifetimePlan.strikethrough_price;
      }
    }
    return null;
  }

  private get shouldShowDiscount(): boolean {
    // If already lifetime no plans exist
    if (this.alreadyLifetime) {
      return false;
    }
    if (this.upgrading) {
      // Regular discounts don't apply to upgrades. We need to check the strikeThroughPrice
      return !!this.strikeThroughPrice;
    }
    // Referral discount takes precedence
    if (this.paywallPlans.annualPlanIncludesReferralDiscount) {
      return true;
    }
    if (this.seasonalDiscount) {
      return true;
    }
    if (this.directTrafficDiscount) {
      return !!this.discount?.is_visible;
    }
    if (this.isStudentDiscount) {
      return true;
    }
    return !!(
      this.is_discounted ||
      (this.paywallPlans.annualPlan && this.paywallPlans.annualPlan.strikethrough_price)
    );
  }

  private get alreadyLifetime(): boolean {
    return this.paywall_type === 'already_lifetime';
  }

  private get upgrading(): boolean {
    return this.paywall_type === 'upgrade';
  }

  private get campaignDiscountOverrideHtml(): string | null {
    if (this.seasonalDiscount || this.isStudentDiscount) {
      return `${this.discount?.name} discount applied`;
    }
    if (this.directTrafficDiscount) {
      return 'Limited time discount applied';
    }
    return null;
  }

  private get discountHtml(): string | null {
    if (this.upgrading) {
      return 'Prorated discount unlocked';
    }
    if (this.paywallPlans.annualPlanIncludesReferralDiscount) {
      return 'Referral discount unlocked!';
    }
    if (this.campaignDiscountOverrideHtml) {
      return this.campaignDiscountOverrideHtml;
    }
    if (this.coupon && this.coupon.code_used_name) {
      return `Discount unlocked for <em>${this.coupon.code_used_name}</em>`;
    }
    if (this.user.days_since_signup !== null && this.user.days_since_signup <= 7) {
      return 'Discount unlocked! Thanks for joining Brilliant.';
    }
    return 'Discount unlocked! Thanks for being active on Brilliant.';
  }

  private get isMobileViewPort(): boolean {
    return this.isMobile(this.$mq);
  }

  private get showOnePaymentWarning(): boolean {
    // during trial
    if (this.user.is_during_trial) {
      return false;
    }
    if (this.paywallPlans.annualPlanIncludesReferralDiscount) {
      return true;
    }
    // trial paywall
    if (this.paywallPlans.annualPlanIsTrialPlan) {
      return false;
    }
    // paywall ONLY includes a monthly plan
    return !(
      !this.paywallPlans.annualPlan &&
      !this.paywallPlans.threeMonthPlan &&
      !this.paywallPlans.sixMonthPlan &&
      !this.paywallPlans.twoYearPlan &&
      !this.paywallPlans.threeYearPlan &&
      !this.paywallPlans.fourYearPlan
    );
  }

  private get onePaymentWarning(): string {
    if (this.paywallPlans.annualPlanIncludesReferralDiscount) {
      return this.referralDiscountActiveOnePaymentWarning;
    }
    return `*Billed as one payment. Subscription renews automatically at the end of term.
        You can turn off auto-renew from your settings. Learn more`;
  }

  private get referralDiscountActiveOnePaymentWarning(): string {
    const intervalMessage = '/month';

    const commonMessage = `*For the first year,
        then ${this.paywallPlans?.annualPlan?.renewal_price}${intervalMessage};
        all terms are billed as one payment.`;

    const remainingMessage = this.paywallPlans.annualPlanIsTrialPlan
      ? ''
      : ` Subscription renews automatically at the end of term. You can turn off auto-renew
            from your settings. Learn more`;

    return `${commonMessage}${remainingMessage}`;
  }
}
